import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { TrendUp, TrendDown, Minus, CaretDown } from "@phosphor-icons/react";

// Neutral band: net within ±this value renders the neutral (grey) state.
export const NET_NEUTRAL_THRESHOLD = 50;
const MINUS = "\u2212";
const pad2 = (n) => String(n).padStart(2, "0");
const isoLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const currentMonthPeriod = () => {
  const now = new Date();
  return {
    type: "month",
    label: "this month",
    start: isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: isoLocal(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
  };
};

export const formatChipAmount = (n) => {
  const abs = Math.abs(Number(n) || 0);
  if (abs >= 1000000) return `$${(abs / 1000000).toFixed(1)}M`;
  const hasCents = Math.round(abs * 100) % 100 !== 0;
  return `$${abs.toLocaleString("en-US", hasCents ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { maximumFractionDigits: 0 })}`;
};

const signedFigure = (net) => {
  if (Math.round((Number(net) || 0) * 100) === 0) return "$0";
  return `${net > 0 ? "+" : MINUS}${formatChipAmount(net)}`;
};

const fmtDay = (isoDate) => {
  const [y, m, d] = (isoDate || "").split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const TONES = {
  positive: { color: "var(--bk-success)", bg: "var(--bk-success-bg)", Icon: TrendUp },
  negative: { color: "#B23B1F", bg: "#FDECE8", Icon: TrendDown },
  neutral: { color: "var(--bk-text-2)", bg: "var(--bk-page)", Icon: Minus },
};

export function NetMovementChip({ businessId, period, refreshToken, variant = "full", testId = "net-movement-chip" }) {
  const p = period || currentMonthPeriod();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [expanded, setExpanded] = useState(false);
  const retriedRef = useRef(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const { data: res } = await api.get(`/businesses/${businessId}/net-movement`, {
        params: { period_start: p.start, period_end: p.end, compare: p.type === "ytd" ? "year" : "month" },
      });
      setData(res);
      setStatus("ready");
      retriedRef.current = false;
    } catch {
      if (!retriedRef.current) {
        retriedRef.current = true;
        timerRef.current = setTimeout(load, 3000);
      } else {
        setStatus("error");
      }
    }
  }, [businessId, p.start, p.end, p.type]);

  useEffect(() => {
    setExpanded(false);
    retriedRef.current = false;
    load();
    return () => clearTimeout(timerRef.current);
  }, [load, refreshToken]);

  const cur = data?.current;
  const prior = data?.prior;
  const net = cur ? cur.net_movement : 0;
  const noData = !!cur && cur.transaction_count === 0 && (cur.transfer_count || 0) === 0;
  const tone = Math.abs(net) <= NET_NEUTRAL_THRESHOLD ? "neutral" : net > 0 ? "positive" : "negative";
  const { color, bg, Icon } = TONES[tone];

  const earliest = data?.earliest_overall_date;
  const partialSince = cur && !noData && earliest && earliest > p.start && earliest < p.end ? earliest : null;

  const noun = p.type === "ytd" ? "year" : p.type === "custom" ? "period" : "month";
  let trendText = "";
  let trendDir = "none";
  if (prior) {
    const priorHasData = prior.transaction_count > 0 || (prior.transfer_count || 0) > 0;
    if (!priorHasData) {
      trendText = "No prior period to compare";
    } else {
      const delta = net - prior.net_movement;
      if (Math.abs(delta) <= 1) {
        trendText = `${MINUS} same as last ${noun}`;
        trendDir = "same";
      } else {
        trendDir = delta > 0 ? "up" : "down";
        trendText = `${delta > 0 ? "\u2191" : "\u2193"} ${formatChipAmount(delta)} vs. last ${noun}`;
      }
    }
  }

  if (variant === "compact") {
    if (status === "error") return null;
    if (status === "loading" || !cur) {
      return <span className="inline-block rounded-full animate-pulse" style={{ width: 190, height: 30, background: "var(--bk-page)" }} data-testid={`${testId}-skeleton`} />;
    }
    if (noData) return null;
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
        style={{ background: bg, color }}
        data-testid={testId}
        title={`In ${formatChipAmount(cur.total_inflows)} · Out ${formatChipAmount(cur.total_outflows)} · Transfers excluded ${formatChipAmount(cur.total_transfer_volume)}`}
      >
        <Icon size={13} weight="bold" />
        Net movement {p.label}: {signedFigure(net)}
      </span>
    );
  }

  if (status === "loading" || (!cur && status !== "error")) {
    return (
      <div className="bk-card p-5" data-testid={`${testId}-skeleton`} style={{ minHeight: 148 }}>
        <div className="animate-pulse space-y-3">
          <div className="h-9 rounded" style={{ width: 160, background: "var(--bk-page)" }} />
          <div className="h-4 rounded" style={{ width: 210, background: "var(--bk-page)" }} />
          <div className="h-3.5 rounded" style={{ width: 170, background: "var(--bk-page)" }} />
          <div className="h-3.5 rounded" style={{ width: 110, background: "var(--bk-page)" }} />
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="bk-card p-5" data-testid={`${testId}-error`} style={{ minHeight: 148 }}>
        <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Could not load net movement</p>
        <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Try refreshing, or check back after your next sync.</p>
      </div>
    );
  }

  if (noData) {
    return (
      <div className="bk-card p-5" data-testid={`${testId}-empty`}>
        <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>
          {p.type === "month" && p.label === "this month" ? "No transactions yet this month" : "No transactions for this period"}
        </p>
        <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Net movement will show up here once money moves.</p>
      </div>
    );
  }

  return (
    <div className="bk-card p-0 overflow-hidden" style={{ borderLeft: `4px solid ${tone === "neutral" ? "var(--bk-border)" : color}` }} data-testid={testId}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid={`${testId}-toggle`}
        className="w-full text-left p-5 transition-colors hover:bg-black/[0.02] cursor-pointer"
        style={{ minHeight: 44 }}
        aria-label={`Net movement ${p.label}: ${signedFigure(net)}. ${trendText}. ${expanded ? "Hide" : "See"} breakdown.`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-display text-3xl font-bold flex items-center gap-2" style={{ color }} data-testid={`${testId}-figure`}>
              <Icon size={24} weight="bold" />
              {signedFigure(net)}
            </p>
            <p className="text-sm font-semibold mt-1" style={{ color: "var(--bk-text)" }} data-testid={`${testId}-period-label`}>
              Net movement {p.label}
            </p>
            <p className="text-xs mt-1.5" style={{ color: "var(--bk-text-2)" }} data-testid={`${testId}-trend`}>{trendText}</p>
            {partialSince && (
              <div className="mt-1.5" data-testid={`${testId}-partial-notice`}>
                <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>Since {fmtDay(partialSince)}</p>
                {trendDir !== "none" && trendText !== "No prior period to compare" && (
                  <p className="text-[11px] mt-0.5 italic" style={{ color: "var(--bk-text-2)" }}>Comparison may not reflect a full prior {noun}.</p>
                )}
              </div>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold shrink-0 mt-1" style={{ color: "var(--bk-accent)" }} data-testid={`${testId}-expand`}>
            {expanded ? "Hide breakdown" : "See breakdown"}
            <CaretDown size={12} weight="bold" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
          </span>
        </div>
      </button>
      {expanded && (
        <div className="px-5 pb-5 pt-4 border-t" style={{ borderColor: "var(--bk-border)" }} data-testid={`${testId}-breakdown`}>
          <div className="flex items-center justify-between py-1 text-sm" data-testid={`${testId}-breakdown-inflows`}>
            <span style={{ color: "var(--bk-text)" }}>Total money in</span>
            <span className="font-semibold" style={{ color: "var(--bk-success)" }}>{formatChipAmount(cur.total_inflows)}</span>
          </div>
          <div className="flex items-center justify-between py-1 text-sm" data-testid={`${testId}-breakdown-outflows`}>
            <span style={{ color: "var(--bk-text)" }}>Total money out</span>
            <span className="font-semibold" style={{ color: "#B23B1F" }}>{formatChipAmount(cur.total_outflows)}</span>
          </div>
          <div className="flex items-center justify-between py-1 text-sm" data-testid={`${testId}-breakdown-transfers`}>
            <span style={{ color: "var(--bk-text)" }}>Transfers excluded</span>
            <span className="font-semibold" style={{ color: "var(--bk-text-2)" }}>{formatChipAmount(cur.total_transfer_volume)}</span>
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }} data-testid={`${testId}-breakdown-footer`}>
            Transfers between your own accounts are excluded from net movement.
          </p>
        </div>
      )}
    </div>
  );
}
